import axios, {AxiosRequestConfig} from 'axios'
import apiClient from '../helpers/apiClient'
import {
  BucketKey,
  GlobalSearchResponse,
  PaginatedSearchResponse,
  ResultBucket,
  ResultItem,
  SearchType,
} from '../pages/search/types'
import {normalizeGlobalResponse} from '../pages/search/utils'
import {getExperts} from './expertsService'

// SearchType -> aggregate bucket key. The wire uses camelCase (mastermindCircles) for the
// bucket, but SearchType is snake_case (mastermind_circles).
const BUCKET_KEY_BY_TYPE: Record<Exclude<SearchType, 'all'>, BucketKey> = {
  members: 'members',
  experiences: 'experiences',
  forums: 'forums',
  mastermind_circles: 'mastermindCircles',
  expertise: 'expertise',
}

const emptyBucket = <T extends ResultItem>(): ResultBucket<T> => ({total: 0, items: []})

/** Wrap a DRF flat envelope in the aggregate shape so the dropdown pipeline (which expects
 *  buckets) works when a specific type was requested. */
const wrapFlatAsAggregate = (
  q: string,
  type: Exclude<SearchType, 'all'>,
  data: any
): GlobalSearchResponse => {
  const items = Array.isArray(data?.results) ? data.results : []
  const total = typeof data?.count === 'number' ? data.count : items.length
  const buckets = {
    members: emptyBucket(),
    experiences: emptyBucket(),
    forums: emptyBucket(),
    mastermindCircles: emptyBucket(),
    expertise: emptyBucket(),
  } as GlobalSearchResponse['buckets']
  const key = BUCKET_KEY_BY_TYPE[type]
  ;(buckets as any)[key] = {total, items}
  return {query: q, buckets}
}

/**
 * Global Search service (Search01).
 *
 * ⚠️ apiClient's baseURL already ends with /api/v1, so the path here is `/search/` (NOT
 * `/api/v1/search/`). Auth bearer token is injected by the apiClient request interceptor.
 *
 * These calls deliberately do NOT route through handleApiError: a header-search failure must not
 * raise a global alert on every keystroke. Errors surface through the hook's `error` state instead.
 *
 * Cancellation: axios 0.21 predates AbortSignal (added in 0.22), so callers pass a CancelToken via
 * `config`. `axios.isCancel(err)` distinguishes a superseded request from a real error.
 */

/** Re-export so callers can build a source without importing axios directly. */
export const createCancelSource = () => axios.CancelToken.source()

/** Aggregate search. When type='all' the backend returns a bucketed shape and we normalise it
 *  directly; when type is a specific value the backend returns a DRF paginated envelope
 *  (`{count, results}`), which we wrap into a single-bucket aggregate so downstream code
 *  (buildDropdownGroups etc.) can treat both cases uniformly. */
export const globalSearch = async (
  q: string,
  type: SearchType = 'all',
  limit?: number,
  offset?: number,
  config?: AxiosRequestConfig
): Promise<GlobalSearchResponse> => {
  // "Expertise" scope surfaces Partner (partner_type='Expert') records via ExpertSearchView,
  // not the aggregator's ExpertiseService rows. Keep it out of the /search/ call and wrap it as
  // a single-bucket aggregate so the dropdown pipeline stays uniform.
  if (type === 'expertise') {
    const rows = await getExperts(q, '')
    const items = rows.map((e) => ({
      type: 'expertise' as const,
      id: e.id,
      companyName: e.company_name,
      title: e.company_name,
      address: e.address ?? undefined,
      logo: e.logo ?? undefined,
      expertiseCount: e.expertise_count,
    }))
    return wrapFlatAsAggregate(q, type, {count: items.length, results: items})
  }
  const params: Record<string, string | number> = {q, type}
  if (limit !== undefined) params.limit = limit
  if (offset !== undefined) params.offset = offset
  const {data} = await apiClient.get('/search/', {params, ...config})
  if (type === 'all') return normalizeGlobalResponse(data)
  return wrapFlatAsAggregate(q, type, data)
}

/**
 * Single-type search (type=<specific> → DRF LimitOffset envelope). Used by the results page for
 * per-section pagination. Params are limit/offset (default page size 5 server-side).
 */
export const searchByType = async (
  type: Exclude<SearchType, 'all'>,
  q: string,
  limit?: number,
  offset?: number,
  config?: AxiosRequestConfig
): Promise<PaginatedSearchResponse<ResultItem>> => {
  const params: Record<string, string | number> = {q, type}
  if (limit !== undefined) params.limit = limit
  if (offset !== undefined) params.offset = offset
  const {data} = await apiClient.get('/search/', {params, ...config})
  return {
    count: typeof data?.count === 'number' ? data.count : (data?.results?.length ?? 0),
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results: Array.isArray(data?.results) ? data.results : [],
  }
}
